/**
 * Integration Service
 * Handles integration-related API calls
 */

import { api } from '@/lib/api';
import { buParams, currentBuId, machineConnectPost } from './integration-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { IntegrationAdapter } from '@/adapters';
import type { IntegrationAccount, IntegrationAccountResponseDto } from '@/types';
import { createFreshdeskIntegrationService } from './freshdesk-integration.service';
import { createIntercomIntegrationService } from './intercom-integration.service';
import type { MetaAuthType } from '@/modules/integrations/meta-contract';
import type { RechargeConnectResult } from '@/modules/integrations/recharge';

/** Mintsoft: a static ("Never Expire") key, or the username + password Kim signs in with. */
export type MintsoftCredentials =
	| { auth_type: 'api_key'; api_key: string }
	| { auth_type: 'credentials'; username: string; password: string };

export type MintsoftConnectInput = MintsoftCredentials & { client_id?: string; client_name?: string };

export interface MintsoftClientList {
	clients: { id: string; name: string; code: string }[];
	/** False for a client user's key, which Mintsoft already scopes to one client. */
	canListClients: boolean;
}

export interface MintsoftConnectResult {
	verified?: boolean;
	warning?: string;
}

/** `GET /integrations/meta/channels` — the connected accounts plus what this deployment can offer. */
export interface MetaChannelConfigResponse {
	success: boolean;
	isConnected: boolean;
	channels: unknown[];
	availableAuthTypes: MetaAuthType[];
}
export type {
	FreshdeskAgent,
	FreshdeskConnectPayload,
	FreshdeskDisconnectResult,
	FreshdeskRotationResult,
	FreshdeskVerificationResult
} from './freshdesk-integration.service';

/** A Richpanel agent the customer can pick as the automated-reply author. */
export interface RichpanelUser {
	id: string;
	name: string | null;
	email: string | null;
}

/** Result of listing Richpanel agents for the reply-user step. */
export interface RichpanelListUsersResult {
	users: RichpanelUser[];
}

/** A single Shopify OAuth scope shown in the consent list. */
export interface ShopifyScope {
	value: string;
	label: string;
	description: string;
	category: string;
	/** Required scopes are always requested and can't be unchecked. */
	required?: boolean;
}

/** One provisioned Richpanel tag from the ensure-tags step. */
export interface RichpanelTagResult {
	key: 'responded' | 'spam';
	id: string;
	name: string;
	created: boolean;
}

/** Result of ensuring the Kim Richpanel tags exist. */
export interface RichpanelEnsureTagsResult {
	tags: RichpanelTagResult[];
}

/** Payload for the final Richpanel connect step — the wizard sends everything at once. */
export interface RichpanelConnectPayload {
	api_key: string;
	webhook_token?: string;
	agent_uuid?: string;
	tags?: Record<string, string>;
}

export type HelpScoutConnectionState = 'disconnected' | 'connecting' | 'connected' | 'needs_attention' | 'failed';

export type HelpScoutReplyIdentityMode = 'dedicated_user' | 'oauth_owner_fallback' | 'unknown';

export type HelpScoutAttentionCode = 'dedicated_user_unavailable' | 'webhook_validation_failed' | 'unknown';

export interface HelpScoutConnectionStatus {
	status: HelpScoutConnectionState;
	companyName?: string;
	inboxCount?: number;
	replyIdentityMode?: HelpScoutReplyIdentityMode;
	webhookConfigured: boolean;
	needsAttention?: boolean;
	attentionCode?: HelpScoutAttentionCode;
	errorCode?: string;
}

interface HelpScoutAuthorizeResponseDto {
	authorizationUrl: string;
	expiresAt?: string;
}

interface HelpScoutConnectionStatusResponseDto {
	status: HelpScoutConnectionState;
	errorCode?: string;
	metadata?: {
		connectedName?: string;
		mailboxCount?: number;
		replyIdentityMode?: string;
		webhookConfigured?: boolean;
		needsAttention?: boolean;
		attentionCode?: string;
		errorCode?: string;
	};
}

const freshdeskIntegrationService = createFreshdeskIntegrationService(api);
const intercomIntegrationService = createIntercomIntegrationService(api);
/** Payload for the Kustomer connect step — the subdomain (org slug) plus the API key. */
export interface KustomerConnectPayload {
	api_key: string;
	subdomain: string;
}

// --- Gladly types ---

/** A Gladly agent the customer can pick as the automated-reply author. */
export interface GladlyUser {
	id: string;
	name: string | null;
	email: string | null;
}

/**
 * Result of listing Gladly agents for the reply-user step.
 *
 * The whole roster, unfiltered — Kim nominates nobody. This used to also carry `botAgent` /
 * `botAgentEmail` so the wizard could auto-adopt `gladly-bot@kim.cc`, but Gladly's Agents API
 * exposes no status field anywhere, so a deactivated agent is indistinguishable from an active
 * one and auto-adoption could silently bind replies to an author that cannot post.
 */
export interface GladlyListUsersResult {
	users: GladlyUser[];
}

/**
 * Credentials every stateless Gladly wizard step carries. The API token authenticates as its
 * owning agent, so machine needs the agent's email as the Basic-auth username alongside it.
 *
 * Field names match machine's request body exactly — it reads `api_token`, not `api_key`.
 */
export interface GladlyStepCredentials {
	organization: string;
	agent_email: string;
	api_token: string;
}

/** Payload for the final Gladly connect step. */
export interface GladlyConnectPayload extends GladlyStepCredentials {
	agent_uuid?: string;
}

export const integrationService = {
	...freshdeskIntegrationService,
	...intercomIntegrationService,
	/**
	 * Get account integrations
	 */
	async getAccountIntegrations(params?: { bu_id?: string }): Promise<IntegrationAccount[]> {
		// No bu_id means every brand's connections, which is what the page needs to
		// group them. Scoped, it returns just that brand's.
		const response = await api.get<IntegrationAccountResponseDto[]>(
			'/machine' + API_ENDPOINTS.INTEGRATIONS.ACCOUNT,
			{ params }
		);
		return IntegrationAdapter.fromApiList(response.data);
	},

	/**
	 * Disconnect an integration by ID
	 */
	async disconnectIntegration(id: string): Promise<void> {
		await api.post('/machine' + API_ENDPOINTS.INTEGRATIONS.DISCONNECT(id));
	},

	/**
	 * Connect Smartrr integration with an API key
	 */
	async connectSmartrrIntegration(apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.SMARTRR.CONNECT, { api_key: apiKey });
	},

	/**
	 * Connect Skio integration with an API key, and optionally a base URL override
	 * (proxies/testing only — the tool server defaults to graphql.skio.com).
	 */
	async connectSkioIntegration(apiKey: string, baseUrl?: string): Promise<void> {
		// Through machineConnectPost like every other connect, so the brand rides along. A raw
		// api.post here connected Skio to the account's DEFAULT brand whatever the operator had
		// selected — silently, since connecting still succeeded.
		await machineConnectPost(API_ENDPOINTS.SKIO.CONNECT, {
			api_key: apiKey,
			...(baseUrl ? { base_url: baseUrl } : {})
		});
	},

	/**
	 * Connect Smile.io integration with an API key. Smile is account-scoped by the key alone —
	 * there is no shop domain or region to collect.
	 */
	async connectSmileIntegration(apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.SMILE.CONNECT, { api_key: apiKey });
	},

	/**
	 * Connect InfoPlus integration with a subdomain and API key
	 */
	async connectInfoplusIntegration(subdomain: string, apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.INFOPLUS.CONNECT, {
			subdomain,
			api_key: apiKey
		});
	},

	/**
	 * Connect ClickPost integration with key, username and password.
	 * Backend calls ClickPost token API and persists token/expires_at/cpIds in auth_meta_data.
	 */
	async connectClickpostIntegration(key: string, username: string, password: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.CLICKPOST.CONNECT, {
			key,
			username,
			password
		});
	},

	/**
	 * Fetch the Shopify scopes the app is allowed to request. The user can pick
	 * which of these to grant before starting the OAuth flow.
	 */
	async getShopifyScopes(): Promise<ShopifyScope[]> {
		const response = await api.get<{ data: ShopifyScope[]; success: boolean }>(
			'/machine' + API_ENDPOINTS.SHOPIFY.SCOPES
		);
		return response.data.data ?? [];
	},

	/**
	 * Connect Shopify integration via OAuth — opens authUrl in a new tab.
	 * Returns the opened window so the caller can detect when the OAuth
	 * flow finishes (the tab closing) and refetch the integration status.
	 *
	 * `scopes` is the explicit list of Shopify OAuth scopes the user selected.
	 */
	/** Make one of the account's Shopify stores the one used when no store is given. */
	async setPrimaryShopifyStore(id: string): Promise<void> {
		await api.post('/machine' + API_ENDPOINTS.SHOPIFY.PRIMARY, { id: Number(id) });
	},

	async connectShopifyIntegration(
		shop: string,
		accountId: string,
		scopes: string[]
	): Promise<Window | null> {
		const response = await api.get<{ authUrl: string; success: boolean }>(
			'/machine' + API_ENDPOINTS.SHOPIFY.INSTALL,
			// `scopes` is omitted when empty (catalog failed to load) so the
			// backend falls back to its default scope set.
			// bu_id travels on to machine, which packs it into the SIGNED OAuth state —
			// Shopify hands that back to an unauthenticated callback, so the brand has
			// to be tamper-evident rather than re-read from a request we do not control.
			{
				params: {
					shop,
					accountId,
					...(scopes.length > 0 ? { scopes: scopes.join(',') } : {}),
					...buParams()
				}
			}
		);
		if (response.data.authUrl) {
			return window.open(response.data.authUrl, '_blank');
		}
		return null;
	},

	/**
	 * Connect Judge.me integration with a domain and API key
	 */
	async connectJudgemeIntegration(domain: string, apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.JUDGEME.CONNECT, { domain, api_key: apiKey });
	},

	async connectAftershipIntegration(apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.AFTERSHIP.CONNECT, { api_key: apiKey });
	},

	async connectWondermentIntegration(apiKey: string): Promise<void> {
		// Same as Skio: a raw api.post bypassed the brand and connected to the account default.
		await machineConnectPost(API_ENDPOINTS.WONDERMENT.CONNECT, { api_key: apiKey });
	},

	async connectCheckoutChampIntegration(loginId: string, password: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.CHECKOUTCHAMP.CONNECT, { login_id: loginId, password });
	},

	async fetchShipbobChannels(pat: string): Promise<{ id: number; name: string; application_name: string }[]> {
		const { data } = await api.post<{ channels: { id: number; name: string; application_name: string }[] }>('/machine' + API_ENDPOINTS.SHIPBOB.CHANNELS, { pat });
		return data.channels;
	},

	async connectShipbobIntegration(pat: string, channelId: number): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.SHIPBOB.CONNECT, { pat, channel_id: channelId });
	},

	/**
	 * Connect Recharge integration with an API key. machine checks the key's scopes and
	 * reports the write scopes it lacks.
	 */
	async connectRechargeIntegration(apiKey: string): Promise<RechargeConnectResult> {
		const response = await machineConnectPost<RechargeConnectResult>(API_ENDPOINTS.RECHARGE.CONNECT, { api_key: apiKey });
		return response.data ?? {};
	},

	/**
	 * Connect ShipStation integration with an API key
	 */
	async connectShipstationIntegration(apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.SHIPSTATION.CONNECT, { api_key: apiKey });
	},

	/**
	 * Connect Yotpo integration (loyalty + reviews credentials in one record)
	 */
	async connectYotpoIntegration(guid: string | undefined, apiKey: string | undefined, appKey: string, secret: string, merchantId?: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.YOTPO.CONNECT, {
			...(guid ? { guid } : {}),
			...(apiKey ? { api_key: apiKey } : {}),
			app_key: appKey,
			secret,
			...(merchantId ? { merchant_id: merchantId } : {})
		});
	},

	async connectSlackIntegration(botToken: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.SLACK.CONNECT, { bot_token: botToken });
	},

	/**
	 * Connect Loop Returns integration with an API key
	 */
	async connectLoopReturnsIntegration(apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.LOOPRETURNS.CONNECT, { api_key: apiKey });
	},

	async connectKlaviyoIntegration(apiKey: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.KLAVIYO.CONNECT, { api_key: apiKey });
	},

	async connectUspsIntegration(clientId: string, clientSecret: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.USPS.CONNECT, { client_id: clientId, client_secret: clientSecret });
	},

	/**
	 * Connect Royal Mail — the client ID/secret of the developer-portal application subscribed to
	 * the Tracking API. Machine verifies the pair against Royal Mail before saving it.
	 */
	async connectRoyalMailIntegration(clientId: string, clientSecret: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.ROYALMAIL.CONNECT, { client_id: clientId, client_secret: clientSecret });
	},

	async connectReviewsioIntegration(apiKey: string, storeId: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.REVIEWSIO.CONNECT, { api_key: apiKey, store_id: storeId });
	},

	async connectTrustpilotIntegration(apiKey: string, businessUnitId?: string, apiSecret?: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.TRUSTPILOT.CONNECT, {
			api_key: apiKey,
			business_unit_id: businessUnitId,
			api_secret: apiSecret
		});
	},

	/**
	 * Start a Meta consent flow and return the popup handle.
	 *
	 * `authType` picks the Meta product: `facebook_login` for an Instagram account linked to a
	 * Facebook Page (the only route that can like comments), `instagram_login` for a standalone
	 * Instagram professional account. They are separate consent screens on separate hosts, so the
	 * choice is made before the popup opens, not inside it.
	 */
	async connectMetaIntegration(authType: MetaAuthType): Promise<Window | null> {
		const response = await api.get<{ authUrl: string; success: boolean }>('/machine' + API_ENDPOINTS.META.INSTALL, {
			params: { auth_type: authType, ...buParams() }
		});
		if (response.data.authUrl) {
			return window.open(response.data.authUrl, 'meta_oauth', 'width=600,height=700');
		}
		return null;
	},

	/**
	 * Which Meta connect routes this deployment can offer.
	 *
	 * Read from machine rather than inferred: the two consent flows are independently configured
	 * and can be switched off per environment, and this is needed most when NOTHING is connected —
	 * which is exactly when the account integrations list carries no `meta` row to read it from.
	 */
	async getMetaChannelConfig(): Promise<MetaChannelConfigResponse> {
		const { data } = await api.get<MetaChannelConfigResponse>('/machine' + API_ENDPOINTS.META.CHANNELS, {
			params: buParams()
		});
		return data;
	},

	/**
	 * Remove one connected Page or Instagram login, leaving the others in place. Machine deletes the
	 * whole integration row once the last credential goes.
	 */
	async removeMetaCredential(credentialId: string): Promise<void> {
		await api.delete('/machine' + API_ENDPOINTS.META.CREDENTIAL(credentialId), { params: buParams() });
	},

	/**
	 * Connect Warehance (WMS). `clientId` applies only to organization-level API keys, which are
	 * scoped to a single client of the 3PL.
	 */
	async connectWarehanceIntegration(apiKey: string, clientId?: string): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.WAREHANCE.CONNECT, {
			api_key: apiKey,
			client_id: clientId
		});
	},

	/** List the 3PL's Mintsoft clients for the client picker. Nothing is stored. */
	async fetchMintsoftClients(credentials: MintsoftCredentials): Promise<MintsoftClientList> {
		const { data } = await api.post<MintsoftClientList>('/machine' + API_ENDPOINTS.MINTSOFT.CLIENTS, credentials);
		return data;
	},

	/**
	 * Connect Mintsoft (WMS) with a static API key or a username + password. machine stores the
	 * details even when Mintsoft rejects them, and reports that as `verified: false` + `warning`.
	 */
	async connectMintsoftIntegration(input: MintsoftConnectInput): Promise<MintsoftConnectResult> {
		const response = await machineConnectPost<MintsoftConnectResult>(API_ENDPOINTS.MINTSOFT.CONNECT, input);
		return response.data ?? {};
	},

	/**
	 * Connect Kustomer integration with a subdomain (org slug) and API key.
	 * Machine stores the subdomain as org_name and provisions the outbound webhook.
	 */
	async connectKustomerIntegration(subdomain: string, apiKey: string): Promise<void> {
		await machineConnectPost(
			API_ENDPOINTS.KUSTOMER.CONNECT,
			{ api_key: apiKey, subdomain },
			'kustomer'
		);
	},

	/** Connect Kim's built-in helpdesk. No credentials; account-scoped, so no bu_id. */
	async connectKimHelpdesk(): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.KIM_HELPDESK.CONNECT, {}, 'helpdesk');
	},

	/**
	 * Disconnect Gorgias integration for an account
	 */
	async disconnectGorgias(accountId: number): Promise<void> {
		await api.post('/machine' + API_ENDPOINTS.GORGIAS.DISCONNECT, { accountId });
	},

	async authorizeHelpScout(): Promise<HelpScoutAuthorizeResponseDto> {
		const { data } = await api.post<HelpScoutAuthorizeResponseDto>('/machine' + API_ENDPOINTS.HELPSCOUT.AUTHORIZE);
		return data;
	},

	async getHelpScoutConnectionStatus(): Promise<HelpScoutConnectionStatus> {
		const { data } = await api.get<HelpScoutConnectionStatusResponseDto>(
			'/machine' + API_ENDPOINTS.HELPSCOUT.CONNECTION_STATUS
		);

		const replyIdentityMode = data.metadata?.replyIdentityMode;
		const attentionCode = data.metadata?.attentionCode;

		return {
			status: data.status,
			companyName: data.metadata?.connectedName,
			inboxCount: data.metadata?.mailboxCount,
			replyIdentityMode: replyIdentityMode === 'dedicated_user' || replyIdentityMode === 'oauth_owner_fallback'
				? replyIdentityMode
				: replyIdentityMode ? 'unknown' : undefined,
			webhookConfigured: data.metadata?.webhookConfigured ?? false,
			needsAttention: data.metadata?.needsAttention,
			attentionCode: attentionCode === 'dedicated_user_unavailable' || attentionCode === 'webhook_validation_failed'
				? attentionCode
				: attentionCode ? 'unknown' : undefined,
			errorCode: data.metadata?.errorCode ?? data.errorCode
		};
	},

	async disconnectHelpScout(): Promise<void> {
		await api.post('/machine' + API_ENDPOINTS.HELPSCOUT.DISCONNECT);
	},

	/**
	 * Connect Zendesk integration via OAuth — opens the consent screen in a popup.
	 * Returns the opened window so the caller can poll for completion, since the
	 * flow finishes in the popup rather than in this tab.
	 */
	async connectZendeskIntegration(subdomain: string): Promise<Window | null> {
		const response = await api.get<{ authUrl: string; success: boolean }>(
			'/machine' + API_ENDPOINTS.ZENDESK.INSTALL,
			// NO BRAND, unlike Shopify. The comment here used to claim machine signs the brand
			// into the OAuth state the way it does for Shopify and Google; it does not —
			// `/zendesk/oauth/install` builds its state from `{ accountId, subdomain }` and
			// nothing else, so the bu_id was accepted and dropped. Zendesk is a helpdesk and
			// belongs to the account, so there is no brand to carry in the first place.
			{ params: { subdomain } });
		if (response.data.authUrl) {
			return window.open(response.data.authUrl, 'zendesk_oauth', 'width=600,height=700');
		}
		return null;
	},

	/**
	 * Connect Richpanel integration with a static API key (x-richpanel-key).
	 * Backend stores the key in auth_meta_data and provisions the per-account webhook_token.
	 */
	/**
	 * Finalize the Richpanel connection — the LAST wizard step. Creates the integrations_platforms
	 * row with everything the wizard collected (api key, minted webhook token, verified agent UUID,
	 * provisioned tag ids) in one write, so an abandoned setup never leaves a partial row.
	 */
	async connectRichpanelIntegration(payload: RichpanelConnectPayload): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.RICHPANEL.CONNECT, payload, 'richpanel');
	},

	/**
	 * List the Richpanel agents so the customer can pick the automated-reply author. Stateless — the
	 * API key is passed directly (the row may not exist yet); machine only reads Richpanel's user list.
	 */
	async listRichpanelUsers(apiKey: string): Promise<RichpanelListUsersResult> {
		const { data } = await api.post<RichpanelListUsersResult>('/machine' + API_ENDPOINTS.RICHPANEL.LIST_USERS, {
			api_key: apiKey
		});
		return data;
	},

	/**
	 * Ensure the Kim tags (responded + spam-auto-close) exist in Richpanel. Stateless — the API key
	 * is passed directly; machine creates only what's missing in Richpanel and returns the tag ids
	 * for the wizard to persist at the final step.
	 */
	async ensureRichpanelTags(apiKey: string): Promise<RichpanelEnsureTagsResult> {
		const { data } = await api.post<RichpanelEnsureTagsResult>('/machine' + API_ENDPOINTS.RICHPANEL.ENSURE_TAGS, {
			api_key: apiKey
		});
		return data;
	},

	// --- Gladly ---

	/**
	 * List Gladly inboxes so the customer can pick one. Stateless — the API key
	 * and organization are passed directly (the row may not exist yet).
	 */
	/**
	 * List Gladly agents so the customer can pick the automated-reply author.
	 * Stateless — the credentials are passed directly.
	 */
	async listGladlyUsers(credentials: GladlyStepCredentials): Promise<GladlyListUsersResult> {
		const { data } = await api.post<GladlyListUsersResult>(
			'/machine' + API_ENDPOINTS.GLADLY.LIST_USERS,
			credentials
		);
		return data;
	},

	/**
	 * Finalize the Gladly connection — the LAST wizard step. Creates the
	 * integrations_platforms row with everything the wizard collected.
	 */
	async connectGladlyIntegration(payload: GladlyConnectPayload): Promise<void> {
		await machineConnectPost(API_ENDPOINTS.GLADLY.CONNECT, payload, 'gladly');
	},

	/**
	 * Connect Google integration — opens OAuth popup, returns nothing.
	 * Caller should poll useIntegrations() until isConnected is true.
	 */
	connectGoogleIntegration(accountId: string): void {
		const backendUrl = import.meta.env.VITE_MACHINE_BASE_URL || 'https://api.dev.kim.cc/api';
		// Google's state is `<accountId>` or `<accountId>:<buId>`. Machine's callback splits
		// on either ':' or '|' and validates the brand against the account; it used to split
		// on '|' alone, which made the account id parse as NaN on every branded connect.
		// A bare accountId keeps working and means account-level.
		const buId = currentBuId();
		const state = buId ? `${accountId}:${buId}` : accountId;
		window.open(
			`${backendUrl}${API_ENDPOINTS.GOOGLE.OAUTH_INSTALL}?state=${encodeURIComponent(state)}`,
			'google_oauth',
			'width=600,height=700',
		);
	},
};
